// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getProviderRejection, handleAPIError } from '@/api/error-utils';
import { Link } from '@/components/link';
import { Info as InfoIcon } from '@/constants/hardfin-standard-icons';
import { sendFulfillmentOrderTo3PL } from '@/fulfillment/api/send-to-3pl';
import { FulfillmentOrder } from '@/fulfillment/api/types';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

const UNHANDLED_3PL_ERROR_MESSAGE =
  "We're sorry but your 3PL's connection failed. Hardfin support has been automatically notified to investigate";

const RECOGNIZED_3PL_ERROR_TYPES = new Set([
  'organization_third_party_fulfillment_not_enabled',
  'location_not_third_party_enabled',
  'location_missing_third_party_identifier',
  'third_party_provider_not_found',
  'third_party_provider_unreachable',
  'third_party_logistics_disabled',
  'location_missing_address',
]);

function isRecognized3PLError(errorType: string): boolean {
  return RECOGNIZED_3PL_ERROR_TYPES.has(errorType);
}

interface Props {
  isOpen: boolean;
  fulfillmentKey: string;
  foNumber: string;
  onClose: () => void;
  onSuccess: (updatedFulfillment: FulfillmentOrder) => void;
  toLocationKey?: string;
  toLocationName?: string;
}

/**
 * Dialog component for sending a fulfillment order to third-party logistics provider.
 * Blocks dismissal while the request is in progress.
 */
export function SendTo3PLDialog(props: Props) {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorType, setErrorType] = useState<string>('');

  const sendMutation = useMutation({
    mutationFn: () => sendFulfillmentOrderTo3PL(props.fulfillmentKey),
    onSuccess: (updatedFulfillment) => {
      close();
      props.onSuccess(updatedFulfillment);
    },
    onError: (error) => {
      const apiError = handleAPIError(error);

      const rejection = getProviderRejection(apiError.providerErrors);
      if (rejection !== undefined) {
        setErrorTitle(rejection.title);
        setErrorMessage(rejection.body);
        setErrorType('');
      } else if (isRecognized3PLError(apiError.errorType)) {
        setErrorTitle('');
        setErrorMessage(apiError.message);
        setErrorType(apiError.errorType);
      } else {
        setErrorTitle('');
        setErrorMessage(UNHANDLED_3PL_ERROR_MESSAGE);
        setErrorType('');
      }
    },
  });

  const handleConfirm = () => {
    setErrorMessage('');
    setErrorTitle('');
    setErrorType('');
    sendMutation.mutate();
  };

  const close = () => {
    props.onClose();
    setErrorMessage('');
    setErrorTitle('');
    setErrorType('');
  };

  // Block dismissal while mutation is in progress
  const handleClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (sendMutation.isPending) {
        return; // Block dismissal during mutation
      }
    }
    close();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2,
        }}
      >
        Send to third-party fulfillment
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Alert
          severity="info"
          icon={<InfoIcon fontSize="medium" />}
          sx={{
            marginY: 3,
            alignItems: 'center',
          }}
        >
          <AlertTitle variant="h6">
            Send order <strong>{props.foNumber}</strong> to 3PL provider?
          </AlertTitle>
          <Typography variant="body1">
            This will create a fulfillment order with your third-party logistics
            provider and change the order status to pending.
          </Typography>
        </Alert>

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
            {errorTitle && <AlertTitle>{errorTitle}</AlertTitle>}
            {errorType === 'location_missing_address' && props.toLocationKey ? (
              <>
                The location you are sending to,{' '}
                <Link
                  to="/locations/$locationKey"
                  params={{ locationKey: props.toLocationKey }}
                >
                  {props.toLocationName ?? 'this location'}
                </Link>
                , is missing an address. The "To location" must have an address
                before the order can be sent to a 3PL for fulfillment. Please
                update the location's address then try sending this order again.
              </>
            ) : (
              errorMessage
            )}
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={close}
          variant="outlined"
          disabled={sendMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={sendMutation.isPending || !!errorMessage}
          sx={{ ml: 1.5 }}
        >
          {sendMutation.isPending ? 'Sending...' : 'Send to 3PL'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
